/**
 * Which clinic payment types a RECORD-ONLY flow may offer.
 *
 * Record-only flows (bulk mark-paid, the Billing tab's batch "Record
 * Payment(s)" modal) write a succeeded Payment row and never charge anything,
 * so every active type the clinic configured in Settings → Invoicing is fair
 * game — including credit/debit card, which clinics use to record a card
 * charged on an external terminal. Only two values stay excluded:
 *
 *  - `payment_link`: sends the client a Stripe payment link — a charge flow,
 *    not a record.
 *  - `credit`: reserved for account-credit application (the backend's
 *    assertPaymentTypeAllowed rejects it outright).
 *
 * EnhancedPaymentDialog's APPLY_CREDIT_SENTINEL needs no entry here — it is a
 * dialog-local synthetic option that never appears in GET /payment-types.
 */
const RECORD_ONLY_EXCLUSIONS = new Set(['payment_link', 'credit']);

export interface PaymentTypeOption {
  value: string;
  label: string;
}

/**
 * The ONLY fallback list in the app. It is not "the payment types" — it is what
 * a method select shows while GET /payment-types is in flight or unreachable
 * (offline). The real list is the clinic's ClinicPaymentType rows, which the
 * backend seeds with cash/check/credit_card/debit_card/e_transfer/write_off/
 * other on first read and which `assertPaymentTypeAllowed` validates against.
 * Keep this a strict subset of those seeds so the fallback can never offer a
 * value the server would reject.
 */
export const STATIC_PAYMENT_TYPES: PaymentTypeOption[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
];

export function filterRecordablePaymentTypes<T extends { value: string }>(types: T[]): T[] {
  return types.filter((t) => !RECORD_ONLY_EXCLUSIONS.has(t.value));
}

/**
 * How much we actually know about the clinic's list right now.
 *
 * An empty array means two completely different things and the old code could
 * not tell them apart: GET /payment-types has not answered yet (or failed), or
 * it answered and the clinic genuinely has no active type left. Falling back to
 * the built-ins in the second case offers cash/check/card — every one of which
 * the server's assertPaymentTypeAllowed then 400s, because GET /payment-types
 * is active-only. So the fallback is now gated on this.
 */
export type PaymentTypesLoadState = 'loading' | 'error' | 'loaded';

/** Map a React Query v3 result to the load state above. */
export function paymentTypesLoadState(query: {
  isLoading?: boolean;
  isError?: boolean;
  isIdle?: boolean;
}): PaymentTypesLoadState {
  if (query.isError) return 'error';
  // React Query v3 only — a query held back by `enabled: false` is `idle`, not
  // `loading`, and has no data. That is still "we don't know yet", so it must
  // keep the fallback instead of reading as a clinic with zero active types.
  if (query.isLoading || query.isIdle) return 'loading';
  return 'loaded';
}

/**
 * Single source of truth for the options a record-only method select offers.
 *
 * Every screen that records a payment against an invoice — the Invoicing
 * screen's inline Payments rows and its bulk "Record Payment(s)" modal, the
 * client invoice wizard — derives its list from here, so a clinic that adds
 * "Venmo" under Settings → Invoicing sees it in all of them. Screens append
 * their own synthetic entries (Send Payment Link, Apply Available Credit)
 * AFTER this call; those are flows, not clinic-configured types.
 *
 * `loadState` defaults to 'loading' so a one-argument call keeps the historical
 * behaviour exactly: an empty/absent list shows the built-in fallback. Pass the
 * real state and a LOADED-empty list returns [] instead — the caller then
 * disables its submit rather than offering four methods the server rejects.
 *
 * `fallback` exists for ONE reason: EnhancedPaymentDialog has always offered a
 * WIDER built-in list (it adds Write-Off and Other, which are seeds too), and
 * that list is what an OFFLINE vet sees, since the fetch fails and the fallback
 * is all there is. Narrowing it to STATIC_PAYMENT_TYPES to reuse this function
 * would have silently removed two working offline methods. Whatever is passed
 * must stay a subset of the server's seeds, exactly like STATIC_PAYMENT_TYPES.
 * It has no effect on the loaded-empty case, which is [] for every caller.
 */
export function recordablePaymentTypeOptions(
  clinicTypes: PaymentTypeOption[] | undefined | null,
  loadState: PaymentTypesLoadState = 'loading',
  fallback: PaymentTypeOption[] = STATIC_PAYMENT_TYPES,
): PaymentTypeOption[] {
  const clinic = clinicTypes ?? [];
  // Loaded and genuinely empty: offer nothing. The built-ins would every one of
  // them 400 on save, so a blank select + a disabled submit is the honest UI.
  const source = clinic.length > 0 ? clinic : loadState === 'loaded' ? [] : fallback;
  return filterRecordablePaymentTypes(source.map((t) => ({ value: t.value, label: t.label })));
}

/**
 * Which option a fresh payment row starts on. 'cash' as before whenever the
 * clinic still offers it; otherwise the first option, so a clinic that
 * deactivated Cash gets a valid selection instead of a blank select that
 * 400s on save.
 */
export function defaultRecordablePaymentType(
  options: PaymentTypeOption[],
  preferred = 'cash',
): string {
  if (options.some((o) => o.value === preferred)) return preferred;
  return options[0]?.value ?? preferred;
}
